import { useMemo, useState } from 'react';
import { cn } from 'lib/cn';
import { PageHeader, Select } from 'components/ui';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { WalletIcon } from 'components/icons';
import { humanizeEnum } from 'lib/formatters';
import { useVendorEarnings, MONTHS } from '../hooks/useVendorEarnings';
import { EarningsSummary } from '../components/EarningsSummary';
import { PayoutList } from '../components/PayoutList';

const MONTH_OPTIONS = MONTHS.map((month) => ({ value: month, label: humanizeEnum(month) }));

const CHANNELS = [
  { key: 'online', label: 'Online', inStore: false },
  { key: 'instore', label: 'In-store', inStore: true },
];

/** Earnings overview: online vs in-store channel, month/year filter, summary and payouts. */
export function EarningsPage() {
  const now = useMemo(() => new Date(), []);
  const [channel, setChannel] = useState('online');
  const [month, setMonth] = useState(MONTHS[now.getMonth()]);
  const [year, setYear] = useState(now.getFullYear());

  const inStore = channel === 'instore';

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 5 }, (_, index) => current - index).map((value) => ({
      value: String(value),
      label: String(value),
    }));
  }, [now]);

  const { earnings, loading, error, refetch } = useVendorEarnings({ month, year, inStore });

  let content;
  if (loading && !earnings) {
    content = <LoadingState label="Loading earnings…" />;
  } else if (error && !earnings) {
    content = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (!earnings) {
    content = (
      <EmptyState
        icon={WalletIcon}
        title="No earnings data"
        description={`No ${inStore ? 'in-store' : 'online'} earnings for this period yet.`}
      />
    );
  } else {
    const hasPayouts =
      (earnings.upcomingPayouts?.length || 0) + (earnings.paidPayouts?.length || 0) > 0;

    content = (
      <div className="space-y-6">
        <EarningsSummary earnings={earnings} showInstoreTotal={inStore} />
        {hasPayouts ? (
          <div className="space-y-6">
            <PayoutList title="Upcoming payouts" payouts={earnings.upcomingPayouts} />
            <PayoutList title="Paid payouts" payouts={earnings.paidPayouts} />
          </div>
        ) : (
          <EmptyState
            icon={WalletIcon}
            title="No payouts yet"
            description="Payouts for this period will appear here once orders are fulfilled."
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Earnings" description="Your payouts and sales by channel and month." />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          className="inline-flex rounded-lg border border-surface-300 bg-white p-1"
          role="tablist"
          aria-label="Earnings channel"
        >
          {CHANNELS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={channel === option.key}
              onClick={() => setChannel(option.key)}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm font-medium transition',
                channel === option.key
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:text-slate-900',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <Select
            options={MONTH_OPTIONS}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            aria-label="Month"
            containerClassName="w-40"
          />
          <Select
            options={yearOptions}
            value={String(year)}
            onChange={(event) => setYear(Number(event.target.value))}
            aria-label="Year"
            containerClassName="w-32"
          />
        </div>
      </div>

      {content}
    </div>
  );
}

export default EarningsPage;
